import React, { useState, useEffect, useCallback } from 'react';
import { apiKeysAPI } from '../services/api';

function ApiKeyManager() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newKeyName, setNewKeyName] = useState('');
    const [creating, setCreating] = useState(false);
    const [newKeyValue, setNewKeyValue] = useState(null);
    const [copied, setCopied] = useState(false);

    const loadKeys = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await apiKeysAPI.getAll();
            if (data.success) setKeys(data.keys);
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao carregar chaves.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadKeys(); }, [loadKeys]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newKeyName.trim()) return;
        try {
            setCreating(true);
            setError(null);
            const { data } = await apiKeysAPI.create(newKeyName.trim());
            if (data.success) {
                setNewKeyValue(data.key);
                setNewKeyName('');
                loadKeys();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao criar chave.');
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (id, active) => {
        try {
            await apiKeysAPI.toggle(id, active);
            loadKeys();
        } catch (err) {
            setError('Erro ao alterar status.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza? Agentes usando esta chave perderao acesso.')) return;
        try {
            await apiKeysAPI.delete(id);
            loadKeys();
        } catch (err) {
            setError('Erro ao deletar chave.');
        }
    };

    const copyKey = () => {
        navigator.clipboard.writeText(newKeyValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="apikeys-manager">
            <div className="apikeys-header">
                <div>
                    <h3>Chaves de API</h3>
                    <p>Crie chaves para agentes externos acessarem o assistente DNS via API.</p>
                </div>
            </div>

            {/* New key reveal */}
            {newKeyValue && (
                <div className="apikey-reveal">
                    <div className="apikey-reveal-header">
                        <strong>Chave criada com sucesso!</strong>
                        <p>Copie agora. Esta chave nao sera exibida novamente.</p>
                    </div>
                    <div className="apikey-reveal-value">
                        <code>{newKeyValue}</code>
                        <button className="btn btn-sm btn-primary" onClick={copyKey}>
                            {copied ? 'Copiado!' : 'Copiar'}
                        </button>
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={() => setNewKeyValue(null)}>
                        Entendi, fechar
                    </button>
                </div>
            )}

            {/* Create form */}
            <form className="apikey-form" onSubmit={handleCreate}>
                <input
                    type="text"
                    placeholder="Nome da chave (ex: meu-agente-ia)"
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    className="apikey-input"
                    disabled={creating}
                />
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!newKeyName.trim() || creating}
                >
                    {creating ? 'Criando...' : 'Criar Chave'}
                </button>
            </form>

            {error && <div className="apikey-error">{error}</div>}

            {/* Usage docs */}
            <div className="apikey-docs">
                <h4>Como usar</h4>
                <p>Envie requisicoes para o endpoint abaixo com sua chave de API:</p>
                <pre className="apikey-code">{`POST /api/v1/chat/completions
Headers:
  X-API-Key: cfk_sua_chave_aqui
  Content-Type: application/json

Body:
{
  "messages": [
    { "role": "user", "content": "Liste todos os dominios" }
  ]
}

Resposta (formato OpenAI-compativel):
{
  "id": "chatcmpl-...",
  "choices": [{
    "message": {
      "role": "assistant",
      "content": "Aqui estao os dominios..."
    }
  }],
  "tool_executions": [...]
}`}</pre>
            </div>

            {/* Keys table */}
            {loading ? (
                <p className="apikey-loading">Carregando...</p>
            ) : keys.length === 0 ? (
                <p className="apikey-empty">Nenhuma chave criada ainda.</p>
            ) : (
                <table className="apikey-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Chave</th>
                            <th>Criada por</th>
                            <th>Criada em</th>
                            <th>Ultimo uso</th>
                            <th>Requests</th>
                            <th>Status</th>
                            <th>Acoes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map(k => (
                            <tr key={k.id} className={!k.active ? 'apikey-row-disabled' : ''}>
                                <td className="apikey-name">{k.name}</td>
                                <td><code className="apikey-prefix">{k.key_prefix}</code></td>
                                <td>{k.created_by_name}</td>
                                <td>{new Date(k.created_at + 'Z').toLocaleDateString('pt-BR')}</td>
                                <td>{k.last_used_at ? new Date(k.last_used_at + 'Z').toLocaleString('pt-BR') : '-'}</td>
                                <td className="apikey-count">{k.request_count}</td>
                                <td>
                                    <span className={`apikey-status ${k.active ? 'active' : 'inactive'}`}>
                                        {k.active ? 'Ativa' : 'Revogada'}
                                    </span>
                                </td>
                                <td className="apikey-actions">
                                    <button
                                        className={`btn btn-sm ${k.active ? 'btn-warning' : 'btn-success'}`}
                                        onClick={() => handleToggle(k.id, !k.active)}
                                    >
                                        {k.active ? 'Revogar' : 'Ativar'}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(k.id)}
                                    >
                                        Deletar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default ApiKeyManager;
